<template>
    <div class="group-booking-reference-object clickable"
         :class="{
             'group-booking-reference-object--new': isNewReference,
             'group-booking-reference-object--none': isNoReference
         }"
         @click="select">
        <!-- Radio button column to select this reference -->
        <div class="form-group">
            <input :checked="selected"
                   class="form-control clickable"
                   type="radio" />
        </div>

        <!-- Name / input field column -->
        <div class="clickable flex--grow">
            <div v-if="isNoReference"
                 class="group-booking-reference-object__display-ref text-muted">
                <em>{{ l('GROUP_BOOKINGS.NO_REFERENCE') }}</em>
            </div>
            <!-- New/edit reference input -->
            <input v-else-if="isEditing || isNewReference"
                   ref="input"
                   v-model="inputValue"
                   type="text"
                   :placeholder="placeholder"
                   class="form-control group-booking-reference-object__input"
                   @input="emitUpdatedReference"
                   @keydown.enter="enterKeyDown" />
            <!-- Display reference -->
            <div v-else
                 class="group-booking-reference-object__display-ref">
                {{ referenceObject.newReference }}
                <span v-if="isRenamed"
                      class="text-lightly-muted">
                    ({{ l('GROUP_BOOKINGS.RENAMED') }})
                </span>
            </div>
        </div>

        <div class="group-booking-reference-object__btns-col">
            <!-- Cancel edit button for existing references -->
            <button v-if="isEditing && !isNewReference"
                    class="round-btn round-btn-grey"
                    @click.prevent="cancelEdit">
                <i class="fa-solid fa-times"></i>
            </button>

            <!-- Save edit button for existing references -->
            <button v-if="isEditing && !isNewReference"
                    class="round-btn round-btn-grey"
                    @click.prevent="saveEdit">
                <i class="fa-solid fa-check"></i>
            </button>

            <!-- Edit button for existing references -->
            <button v-if="!isEditing && !isNewReference && !isNoReference"
                    class="round-btn round-btn-grey"
                    @click.prevent="startEdit">
                <i class="fa-solid fa-pencil"></i>
            </button>

            <!-- Add button for new reference -->
            <!-- This is only visual and doesn't do anything. -->
            <!-- no-pointer-events class allows the click event to bubble up to the parent div to select the row
            when clicked. Normally clicks on disabled buttons are suppressed. -->
            <button v-if="isNewReference"
                    class="round-btn round-btn-grey no-pointer-events"
                    disabled>
                <i class="fa-solid fa-plus"></i>
            </button>
        </div>
    </div>
</template>

<script>
import { langFilter } from '@/vue/filters/langFilter';
import { timeoutPromise } from '@/functions/functions';

export default {
    props: {
        isNewReference: {
            type: Boolean,
            default: false,
        },

        isNoReference: {
            type: Boolean,
            default: false,
        },

        /**
         * Existing reference object to display or edit.
         *
         * @type {GroupBookingReferenceObject}
         */
        referenceObject: {
            type: Object,
            required: false,
            default: null,
        },

        selected: {
            type: Boolean,
            default: false,
        },
    },

    emits: [
        'select',
        'update',
        'submit',
    ],

    data() {
        return {
            isEditing: this.isNewReference,

            inputValue: this.referenceObject ? this.referenceObject.newReference : '',
        };
    },

    computed: {
        placeholder() {
            if (this.isNewReference) {
                return langFilter('GROUP_BOOKINGS.PLACEHOLDER__NEW');
            }

            return langFilter('GROUP_BOOKINGS.PLACEHOLDER__EDIT');
        },

        isRenamed() {
            return this.referenceObject?.originalReference !== this.referenceObject.newReference;
        },
    },

    watch: {
        selected(newValue) {
            // If another reference gets selected, we should stop editing.
            if (!newValue && this.isEditing && !this.isNewReference) {
                this.cancelEdit();
            }

            if (newValue && this.isNewReference) {
                this.focusInput();
            }
        },

        referenceObject(newValue) {
            // If the reference object changes, we should reset the input value.
            this.inputValue = newValue.newReference;
            this.isEditing = false;
            // Note this is specifically not using a deep watcher,
            // as we don't want to reset if the value changes but the reference object remains the same.
        },
    },

    async mounted() {
        await timeoutPromise(100);
        if (this.isNewReference && this.selected) {
            this.focusInput();
        }
    },

    methods: {
        select() {
            this.$emit('select', this.referenceObject);
            this.focusInput();
        },

        focusInput() {
            this.$nextTick(() => {
                this.$refs.input?.focus();
            });
        },

        startEdit() {
            // Small timeout because there is the click handler on the parent div that triggers a select event,
            // which will clear our isEditing flag.
            setTimeout(() => {
                this.isEditing = true;
                this.inputValue = this.referenceObject.newReference;
                this.focusInput();
            });
        },

        saveEdit() {
            const trimmedValue = this.inputValue.trim();
            if (!trimmedValue) {
                return;
            }

            this.emitUpdatedReference();

            this.isEditing = false;
        },

        emitUpdatedReference() {
            const trimmedValue = this.inputValue.trim();

            this.$emit('update', {
                referenceObject: this.referenceObject,
                newReference: trimmedValue,
            });
        },

        cancelEdit() {
            this.isEditing = false;

            // Revert to the original reference.
            this.inputValue = this.referenceObject.originalReference || '';
            this.emitUpdatedReference();
        },

        enterKeyDown() {
            if (this.isNewReference) {
                this.$emit('submit');
            } else if (this.isEditing) {
                this.saveEdit();
            }
        },
    },
};
</script>

<style lang="less" scoped>
@import (less, reference) "../../../../less/variables.less";

.group-booking-reference-object {
    height: 65px;
    border: 1px solid @btn-default-border;
    display: flex;
    align-items: center;

    &:first-child {
        border-top-left-radius: @border-radius-base;
        border-top-right-radius: @border-radius-base;
    }

    &:last-child {
        border-bottom-left-radius: @border-radius-base;
        border-bottom-right-radius: @border-radius-base;
    }

    &:not(:first-child) {
        border-top: 0;
    }

    &--new {
        &:not(:last-child) {
            border-bottom: 2px dashed @btn-default-border;
        }
    }

    &:has(+.group-booking-reference-object--none) {
        // Dashed border on the last selectable reference before the "No reference" option.
        border-bottom: 2px dashed @btn-default-border;
    }

    &__display-ref {
        position: relative;
        top: 1px;
    }

    &__input {
        height: 42px;

        // Make the text in the input somewhat align with the existing options.
        padding-left: 8px;
        margin-left: -9px;
        margin-top: 0px;
    }

    > div {
        padding: @gutter-width 0 @gutter-width @gutter-width;
    }

    &__btns-col {
        flex-shrink: 0;
        display: flex;
        gap: @gutter-width;
        padding-right: @gutter-width !important;
        white-space: nowrap;
    }
}
</style>
